import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  AppSvgIconComponent,
  BaseOverlayComponent,
  SignalButton,
} from '@vallift/ngl';
import type { FormPage } from '../../models/built-form';
import {
  applyPageSequence,
  pageDisplayLabel,
  sortedPages,
} from '../../utils/form-pages';

export interface FormPagesOverlayData {
  pages: FormPage[];
  activePageId: string;
}

export interface FormPagesOverlayResult {
  pages: FormPage[];
  activePageId: string;
}

@Component({
  selector: 'app-form-pages-overlay',
  standalone: true,
  imports: [
    AppSvgIconComponent,
    BaseOverlayComponent,
    DragDropModule,
    SignalButton,
  ],
  templateUrl: './form-pages-overlay.html',
  styleUrl: './form-pages-overlay.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormPagesOverlayComponent {
  private readonly dialogRef = inject(DialogRef<FormPagesOverlayResult | null>);
  private readonly dialogData = inject<FormPagesOverlayData>(DIALOG_DATA);

  readonly pages = signal<FormPage[]>(
    sortedPages(this.dialogData.pages).map((page) => ({
      ...page,
      cta: { ...page.cta },
      fields: [...page.fields],
    })),
  );
  readonly activePageId = signal(this.dialogData.activePageId);

  readonly pageDisplayLabel = pageDisplayLabel;

  onDrop(event: CdkDragDrop<FormPage[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    this.pages.update((current) => {
      const next = [...current];
      moveItemInArray(next, event.previousIndex, event.currentIndex);
      return applyPageSequence(next);
    });
  }

  selectPage(pageId: string): void {
    this.activePageId.set(pageId);
    this.dialogRef.close({
      pages: applyPageSequence(this.pages()),
      activePageId: pageId,
    });
  }

  close(): void {
    this.dialogRef.close(null);
  }

  done(): void {
    this.dialogRef.close({
      pages: applyPageSequence(this.pages()),
      activePageId: this.activePageId(),
    });
  }
}
